import { Suspense, lazy, useMemo } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { RoleGuard, categoryIntent, useMe } from '@most/auth';
import { useWs, type WsInvalidationMap } from '@most/ws-client';
import { PageTransition } from '@most/ui';
import { Shell } from './components/shell/Shell';
import { AccountUnavailable, Forbidden, MustChangePassword, NotFound } from './components/shell/Interstitials';
import { PageSkeleton } from './components/shell/PageSkeleton';
import { SupportChat } from './components/chat/SupportChat';
import { chatEvents } from './api/chatEvents';
import { qk } from './api/keys';

// Route-level code splitting (docs/06 §11). framer-motion is no longer confined to the cart: the
// route-change crossfade (`PageTransition`, below) lives in the always-mounted shell, so the
// `motion` vendor chunk is now on the catalog's critical path. It stays a SEPARATE, cacheable
// chunk (vite.config.ts `manualChunks`) that loads in parallel, so the < 2 s catalog budget on a
// regional connection is met by the parallel fetch, not by keeping the engine out of the bundle.
const CatalogPage = lazy(() => import('./pages/CatalogPage'));
const ProductPage = lazy(() => import('./pages/ProductPage'));
const CartPage = lazy(() => import('./pages/CartPage'));
const OrdersPage = lazy(() => import('./pages/OrdersPage'));
const OrderDetailPage = lazy(() => import('./pages/OrderDetailPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const NotificationsPage = lazy(() => import('./pages/NotificationsPage'));

export function App() {
  return (
    <RoleGuard
      role="buyer"
      fallback={<PageSkeleton />}
      forbidden={<Forbidden />}
      blocked={<AccountUnavailable />}
      mustChangePassword={<MustChangePassword />}
    >
      <BuyerApp />
    </RoleGuard>
  );
}

function BuyerApp() {
  const { data: me } = useMe();

  /**
   * The WS bridge. Every event ends in an INVALIDATION, never a cache write: the frame tells us
   * that something changed, and the value the buyer finally sees is re-fetched through the
   * buyer-audience REST mapper. A frame written straight into the cache would be a second,
   * unaudited serialization path into the UI (plans.md §6).
   */
  const invalidate: WsInvalidationMap = useMemo(
    () => ({
      'notification.new': [qk.notifications, qk.me],
      'notification.unread_count': [qk.me],
      'order.status_changed': [['orders'], qk.notifications, qk.me],
      // Cart sync across the buyer's own tabs.
      'cart.updated': [qk.cart],
      'cart.recalculated': [qk.cart],
      // Support chat: refetch the thread/badge through the buyer mapper.
      'chat.message': [qk.chatMessages, qk.chat],
      'chat.read': [qk.chatMessages],
    }),
    [],
  );

  useWs({
    enabled: !!me,
    invalidate,
    // Call frames need more than an invalidation — the widget's call engine reacts to the
    // id. Forwarded through a tiny bus; still only {type, id}, never data.
    onEvent: (event) => {
      if (event.type.startsWith('call.') || event.type.startsWith('chat.')) {
        chatEvents.emit(event);
      }
    },
  });

  // The crossfade keys on `location.pathname`, and `<Routes location={location}>` is handed the
  // SAME location so the outgoing screen keeps rendering its old match until the exit finishes —
  // otherwise React Router swaps the content the instant the URL changes and there is nothing
  // left to animate out. Keying on pathname (not the whole location) means a `?page=` change on
  // the catalog does not trigger a full-screen fade.
  const location = useLocation();

  return (
    <Shell>
      <Suspense fallback={<PageSkeleton />}>
        <PageTransition routeKey={location.pathname}>
          <Routes location={location}>
            <Route path="/" element={<HomeRedirect />} />
            <Route path="/catalog" element={<CatalogPage />} />
            <Route path="/catalog/:categorySlug" element={<CatalogPage />} />
            <Route path="/products/:id" element={<ProductPage />} />
            <Route path="/cart" element={<CartPage />} />
            <Route path="/orders" element={<OrdersPage />} />
            <Route path="/orders/:number" element={<OrderDetailPage />} />
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/notifications" element={<NotificationsPage />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </PageTransition>
      </Suspense>
      {me && <SupportChat />}
    </Shell>
  );
}

/**
 * `/` → the catalog. A guest who pressed "Заказать" on a landing product card arrives as
 * `/?category=<slug>` (the landing may not spell the catalog route itself — ANON-G-02), so the
 * slug is turned into the category route here. An unknown slug just shows the whole catalog.
 */
function HomeRedirect() {
  const slug = categoryIntent(useLocation().search);
  return <Navigate to={slug ? `/catalog/${slug}` : '/catalog'} replace />;
}
